import {useState} from "react";
import {Button, Group, Menu, Modal, Stack, TextInput} from "@mantine/core";
import {useDisclosure} from "@mantine/hooks";
import IconTrash from "../../../../assets/trash.svg?react";
import IconPencil from "../../../../assets/pencil.svg?react";
import {useNavigate, useParams} from "react-router-dom";
import {chatApi} from "../../chatApi";
import {useTranslation} from "react-i18next";

interface Props {
    id: string;
    name: string;
    children: React.ReactNode;
    onOpen: () => void;
    onClose: () => void;
}

const ThreadButtonMenu: React.FC<Props> = ({id, name, children, onOpen, onClose}) => {
    const {t} = useTranslation();
    const {threadId} = useParams();
    const [deleteThread] = chatApi.endpoints.deleteThread.useMutation();
    const [renameThread, renameResult] = chatApi.endpoints.renameThread.useMutation();
    const navigate = useNavigate();
    const [renameOpened, {open: openRename, close: closeRename}] = useDisclosure(false);
    const [newName, setNewName] = useState(name);

    const trimmed = newName.trim();
    const isDisabled = !trimmed || trimmed === name.trim() || renameResult.isLoading;

    const handleRenameClick = (e: React.MouseEvent<HTMLButtonElement>) => {
        e.stopPropagation();
        setNewName(name);
        openRename();
        onClose();
    };

    const handleRenameConfirm = () => {
        if (isDisabled) return;
        renameThread({threadId: id, name: trimmed});
        closeRename();
    };

    const handleDelete = (e: React.MouseEvent<HTMLButtonElement>) => {
        e.stopPropagation();
        deleteThread(id);
        onClose();

        if (threadId === id) {
            navigate("/");
        }
    };

    return (
        <>
            <Menu
                position="bottom-start"
                onOpen={onOpen}
                onClose={onClose}
                closeOnClickOutside
                closeOnEscape
            >
                <Menu.Target>{children}</Menu.Target>
                <Menu.Dropdown>
                    <Menu.Item
                        leftSection={<IconPencil width={16} />}
                        onClick={handleRenameClick}
                    >
                        {t("common.rename")}
                    </Menu.Item>
                    <Menu.Item
                        leftSection={<IconTrash width={16} />}
                        onClick={handleDelete}
                    >
                        {t("common.delete")}
                    </Menu.Item>
                </Menu.Dropdown>
            </Menu>

            <Modal
                opened={renameOpened}
                onClose={closeRename}
                title={t("rename.modal.title")}
                size="sm"
                centered
                onClick={(e) => e.stopPropagation()}
            >
                <Stack gap="md">
                    <TextInput
                        label={t("rename.modal.label")}
                        value={newName}
                        onChange={(e) => setNewName(e.currentTarget.value)}
                        onKeyDown={(e) => {
                            if (e.key === "Enter" && !isDisabled) handleRenameConfirm();
                            if (e.key === "Escape") closeRename();
                        }}
                        placeholder={name}
                        autoFocus
                        data-autofocus
                        maxLength={200}
                    />
                    <Group justify="flex-end" gap="xs">
                        <Button variant="subtle" color="dibblue" onClick={closeRename}>
                            {t("common.cancel")}
                        </Button>
                        <Button
                            color="dibblue"
                            onClick={handleRenameConfirm}
                            disabled={isDisabled}
                            loading={renameResult.isLoading}
                        >
                            {t("common.save")}
                        </Button>
                    </Group>
                </Stack>
            </Modal>
        </>
    );
};

export default ThreadButtonMenu;
